import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowUpRightIcon } from 'lucide-react';
import { usePrefersReducedMotion } from '../../hooks/useMedia';
import type { ChatMessage, ConsultantAction } from '../../lib/consultant/types';

/** Renders **bold** spans inside a single line of consultant copy. */
function inline(text: string) {
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, index) => part.startsWith('**') && part.endsWith('**') ? <strong key={index} className="font-semibold text-ink" data-id="element-80" data-mp="17dd90ur88ofi">
        {part.slice(2, -2)}
      </strong> : <React.Fragment key={index} data-id="element-81" data-mp="1l4i1ncw6gggc">{part}</React.Fragment>);
}
function Body({
  text
}: {
  text: string;
}) {
  const lines = text.split('\n');
  return <div className="space-y-1.5" data-id="element-82" data-mp="1h50veh8xffa4">
      {lines.map((line, index) => {
      if (line.trim() === '') return <div key={index} className="h-1.5" aria-hidden="true" data-id="element-83" data-mp="1q3ctt5g1bhd4" />;
      if (line.trim().startsWith('•')) {
        return <p key={index} className="flex gap-2.5 pl-0.5 text-[13.5px] leading-relaxed" data-id="element-84" data-mp="z3v329in1ch4">
              <span className="mt-[0.55em] h-1 w-1 shrink-0 rotate-45 bg-brand" aria-hidden="true" data-id="element-85" data-mp="141mt0xzq2oio" />
              <span data-id="element-86" data-mp="190pzmmvcp2sr">{inline(line.replace(/^\s*•\s*/, ''))}</span>
            </p>;
      }
      return <p key={index} className="text-[13.5px] leading-relaxed" data-id="element-87" data-mp="1d3w2rln62dv0" data-mpi={index}>
            {inline(line)}
          </p>;
    })}
    </div>;
}
type Props = {
  message: ChatMessage;
  onAction: (action: ConsultantAction) => void;
};
export function ConsultantMessage({
  message,
  onAction
}: Props) {
  const reduced = usePrefersReducedMotion();
  const isUser = message.role === 'user';
  return <motion.div className={`flex flex-col gap-2.5 ${isUser ? 'items-end' : 'items-start'}`} initial={reduced ? {
    opacity: 1
  } : {
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.45,
    ease: [0.16, 1, 0.3, 1]
  }} data-id="element-88" data-mp="1wr3jvr7p1xcs">
      <div className={isUser ? 'max-w-[85%] rounded-2xl rounded-tr-md bg-brand px-4 py-2.5 text-[13.5px] leading-relaxed text-white' : 'max-w-[92%] rounded-2xl rounded-tl-md border border-line bg-surface/70 px-4 py-3 text-ink'} data-id="element-89" data-mp="5p8xj9l73m0i">
        {isUser ? message.text : <Body text={message.text} data-id="element-90" data-mp="antxr90g65oy" />}
      </div>

      {message.actions && message.actions.length > 0 ? <div className="flex max-w-full flex-wrap gap-2" data-id="element-91" data-mp="pcgnm4npafuf">
          {message.actions.map(action => {
        const base = 'label inline-flex items-center gap-1.5 rounded-full border border-line bg-surface2/60 px-3 py-1.5 text-muted transition-colors hover:border-brand hover:text-brand';
        if (action.kind === 'link') {
          return <a key={action.label} href={action.value} target="_blank" rel="noreferrer noopener" className={base} data-cursor="hover" data-id="element-92" data-mp="3051b83fpbfzg">
                  {action.label}
                  <ArrowUpRightIcon className="h-3 w-3" aria-hidden="true" data-id="element-93" data-mp="1fmz7a9dsx7c4" />
                </a>;
        }
        if (action.kind === 'route') {
          return <Link key={action.label} to={action.value} className={base} data-cursor="hover" data-id="element-94" data-mp="15xfo12m6fbgq">
                  {action.label}
                  <ArrowUpRightIcon className="h-3 w-3" aria-hidden="true" data-id="element-95" data-mp="rxel7hmcptym" />
                </Link>;
        }
        return <button key={action.label} type="button" onClick={() => onAction(action)} className={base} data-cursor="hover" data-id="element-96" data-mp="wuoa8w5tw3lx">
                {action.label}
              </button>;
      })}
        </div> : null}
    </motion.div>;
}